import {
  AiAssessmentScaleResponseDto,
  AiAssignmentResponseDto,
  AiResistantHomeworkResponseDto,
  BloomsTaxonomyActivitiesResponseDto,
  ChoiceBoardResponseDto,
  CollaborativeGroupWorkResponseDto,
  CommonMisconceptionResponseDto,
  EssentialQuestionsResponseDto,
  ExitTicketGeneratorResponseDto,
  FormativeAssessmentResponseDto,
  GenerateToolFromNodeRequestDtoTargetToolType,
  HomeworkIdeaResponseDto,
  LessonHookResponseDto,
  MindmapNodeToolInfo,
  PerformanceTaskResponseDto,
  PodcastResponseDto,
  ProjectBasedLearningResponseDto,
  RealWorldConnectionResponseDto,
  TransferActivityResponseDto,
  VocabularyListResponseDto,
} from "@packages/api/madlenAPI.schemas";
import AiSupportedTasksCard from "@features/tools/components/ai-supported-tasks/AiSupportedTasksCard";
import { AiHomeworkIdeaCard } from "@features/tools/components/ai-resistant-homework-ideas/AiHomeworkIdeaCard";

import PodcastAudioCard from "@features/media-academy/components/PodcastAudioCard";
import AiAssessmentScaleCard from "@features/tools/components/ai-assessment-scale/AiAssessmentScaleCard";
import AiAssignmentCard from "@features/tools/components/ai-assignment-designer/AiAssignmentCard";
import BloomTaxonomyCard from "@features/tools/components/blooms-taxonomy-activity/BloomTaxonomyCard";
import ChoiceBoardCard from "@features/tools/components/choice-boards/ChoiceBoards";
import CollaborativeCard from "@features/tools/components/collaborative/CollaborativeCard";
import MisconceptionCard from "@features/tools/components/misconceptions/MisconceptionCard";
import { EssentialQuestionsCard } from "@features/tools/components/essential-questions/EssentialQuestionsCard";
import { ExitTicketCard } from "@features/tools/components/exit_tickets/ExitTicketCard";
import FormativeAssessmentCard from "@features/tools/components/formative-assessment/FormativeAssessmentCard";
import { HomeworkIdeaCard } from "@features/tools/components/homework_ideas/HomeworkIdeaCard";
import { HookCard } from "@features/tools/components/hooks/HookCard";
import { PerformanceTaskCard } from "@features/tools/components/performance-task/PerformanceTaskCard";
import ProjectBasedLearningCard from "@features/tools/components/project-based-learning/ProjectBasedLearningCard";
import { ConnectionCard } from "@features/tools/components/connections/ConnectionCard";
import { TransferActivityCard } from "@features/tools/components/transfer-activity/TransferActivityCard";

import SingleVocabulary from "@features/tools/components/vocabularies/SingleVocabulary";

const DetailCard = ({ tool }: { tool: MindmapNodeToolInfo }) => {
  if (!tool.id) return null;
  //article gen? image gen?
  switch (tool.toolType) {
    case GenerateToolFromNodeRequestDtoTargetToolType.AI_SUPPORTED_TASKS:
      return <AiSupportedTasksCard tool={tool} />;
    case GenerateToolFromNodeRequestDtoTargetToolType.AI_RESISTANT_HOMEWORK: {
      const homeworkIdea = tool as AiResistantHomeworkResponseDto;
      return (
        <AiHomeworkIdeaCard
          homeworkIdea={{
            videoUrl: homeworkIdea.videoUrl || "",
            id: homeworkIdea.id!.toString(),
            imageUrl: homeworkIdea.imageUrl || "",
            title: homeworkIdea.title || "",
            topic: homeworkIdea.title || homeworkIdea.prompt || "",
            subject: homeworkIdea.courseName || "",
            gradeLevel: homeworkIdea.gradeLevel || 0,
            duration: "45",
            difficulty: "medium",
            description: homeworkIdea.description || "",
            ideas: Array.isArray(homeworkIdea.reportIdeas)
              ? homeworkIdea.reportIdeas
              : [],
            authorName: homeworkIdea.authorName || "",
            authorId: homeworkIdea.authorId?.toString() || "",
            likeCount: homeworkIdea.likeCount || 0,
            didUserLiked: homeworkIdea.didUserLiked || false,
            createdAt: homeworkIdea.createdAt || "",
            status: homeworkIdea.status || "",
          }}
        />
      );
    }
    case GenerateToolFromNodeRequestDtoTargetToolType.AI_ASSESSMENT_SCALE:
      return (
        <AiAssessmentScaleCard
          assessmentScale={tool as AiAssessmentScaleResponseDto}
        />
      );
    case GenerateToolFromNodeRequestDtoTargetToolType.AI_ASSIGNMENT:
      return <AiAssignmentCard assignment={tool as AiAssignmentResponseDto} />;
    case GenerateToolFromNodeRequestDtoTargetToolType.BLOOMS_TAXONOMY_ACTIVITIES:
      return (
        <BloomTaxonomyCard
          bloomsTaxonomyActivity={tool as BloomsTaxonomyActivitiesResponseDto}
        />
      );

    case GenerateToolFromNodeRequestDtoTargetToolType.CHOICE_BOARD:
      return <ChoiceBoardCard tool={tool as ChoiceBoardResponseDto} />;

    case GenerateToolFromNodeRequestDtoTargetToolType.COLLABORATIVE_GROUP_WORK:
      return (
        <CollaborativeCard
          collaborative={tool as CollaborativeGroupWorkResponseDto}
        />
      );

    case GenerateToolFromNodeRequestDtoTargetToolType.COMMON_MISCONCEPTION:
      return (
        <MisconceptionCard
          misconception={tool as CommonMisconceptionResponseDto}
        />
      );

    case GenerateToolFromNodeRequestDtoTargetToolType.ESSENTIAL_QUESTIONS: {
      const eq = tool as EssentialQuestionsResponseDto;
      return (
        <EssentialQuestionsCard
          id={eq.id}
          author={eq.authorName || ""}
          title={eq.title || ""}
          description={eq.description || ""}
          questions={eq.questions || []}
          activitySuggestions={eq.activitySuggestions || []}
          imageUrl={eq.imageUrl!}
          videoUrl={eq.videoUrl}
          courseName={eq.courseName!}
          gradeLevel={eq.gradeLevel!}
          topic={eq.prompt || eq.title || ""}
          isImageLoading={false}
          createdAt={eq.createdAt ?? ""}
        />
      );
    }

    case GenerateToolFromNodeRequestDtoTargetToolType.EXIT_TICKET_GENERATOR: {
      const et = tool as ExitTicketGeneratorResponseDto;
      return (
        <ExitTicketCard
          toolId={et.id}
          status={et.status}
          isEditable={false}
          authorName={et.authorName || ""}
          authorId={et.authorId}
          exitTickets={et.exitTickets || []}
          imageUrl={et.imageUrl!}
          videoUrl={et.videoUrl}
          courseName={et.courseName!}
          gradeLevel={et.gradeLevel!}
          topic={et.title || et.prompt || ""}
          isImageLoading={false}
          createdAt={et.createdAt ?? ""}
        />
      );
    }
    case GenerateToolFromNodeRequestDtoTargetToolType.FORMATIVE_ASSESSMENT: {
      return (
        <FormativeAssessmentCard
          assessment={tool as FormativeAssessmentResponseDto}
        />
      );
    }
    case GenerateToolFromNodeRequestDtoTargetToolType.HOMEWORK_IDEA: {
      return (
        <HomeworkIdeaCard
          homeworkIdea={tool as HomeworkIdeaResponseDto}
          isImageLoading={false}
        />
      );
    }
    case GenerateToolFromNodeRequestDtoTargetToolType.PODCAST: {
      const podcastData = tool as PodcastResponseDto;
      return (
        <PodcastAudioCard
          id={podcastData.id}
          url={podcastData.url}
          name={podcastData.title}
          transcript={podcastData.transcript}
          createdAt={podcastData.createdAt}
          imageUrl={podcastData.imageUrl}
          author={podcastData.authorName}
          // podcastGenerationType={podcastData.}
          questions={podcastData.questions as any}
          openPdfCustomizationModal={undefined}
          videoUrl={podcastData.videoUrl}
        />
      );
    }
    case GenerateToolFromNodeRequestDtoTargetToolType.LESSON_HOOK: {
      return (
        <HookCard
          lessonHook={tool as LessonHookResponseDto}
          isImageLoading={false}
        />
      );
    }
    case GenerateToolFromNodeRequestDtoTargetToolType.PERFORMANCE_TASK: {
      const pt = tool as PerformanceTaskResponseDto;
      return (
        <PerformanceTaskCard
          id={pt.id?.toString()}
          authorName={pt.authorName ?? ""}
          performanceCriteria={pt.performanceCriteria || []}
          imageUrl={pt.imageUrl!}
          videoUrl={pt.videoUrl}
          courseName={pt.courseName!}
          gradeLevel={pt.gradeLevel!}
          prompt={pt.title || pt.prompt || ""}
          description={pt.description || ""}
          studentInstructions={pt.studentInstructions || ""}
          materialsNeeded={pt.materialsNeeded || []}
          isImageLoading={false}
          createdAt={pt.createdAt ?? ""}
        />
      );
    }
    case GenerateToolFromNodeRequestDtoTargetToolType.PROJECT_BASED_LEARNING: {
      return (
        <ProjectBasedLearningCard
          project={tool as ProjectBasedLearningResponseDto}
        />
      );
    }

    case GenerateToolFromNodeRequestDtoTargetToolType.REAL_WORLD_CONNECTION: {
      const conn = tool as RealWorldConnectionResponseDto;
      return (
        <ConnectionCard
          id={conn.id}
          courseName={conn.courseName!}
          prompt={conn.title || conn.prompt!}
          gradeLevel={conn.gradeLevel!}
          connections={conn.connections || []}
          imageUrl={conn.imageUrl!}
          videoUrl={conn.videoUrl}
          isImageLoading={false}
          createdAt={conn.createdAt ?? ""}
          author={conn.authorName!}
        />
      );
    }
    case GenerateToolFromNodeRequestDtoTargetToolType.TRANSFER_ACTIVITY: {
      return (
        <TransferActivityCard
          transferActivity={tool as TransferActivityResponseDto}
        />
      );
    }

    case GenerateToolFromNodeRequestDtoTargetToolType.VOCABULARY_LIST: {
      const vocab = tool as VocabularyListResponseDto;
      return (
        <SingleVocabulary
          id={vocab.id}
          vocabulary={vocab.vocabulary || []}
          courseName={vocab.courseName}
          gradeLevel={vocab.gradeLevel}
          topic={vocab.title || vocab.prompt || ""}
          imageUrl={vocab.imageUrl}
          author={vocab.authorName}
          createdAt={vocab.createdAt}
        />
      );
    }

    default:
      return null;
  }
};

export default DetailCard;
